import { useState, useEffect, useRef } from 'react'
import { getOurCauses } from '@services/CausesService'
import { getEventDonations, getEvents } from '@services/EventsService'
import { Org } from '@Interfaces/Data/OrganizationInterface'
import { Event } from '@Interfaces/Data/EventInterface'
import { Cause } from '@Interfaces/Data/CauseInterface'
import { Donation } from '@Interfaces/Data/DonationInterface'
import { UseEventDataReturn } from '@Interfaces/Hooks/DataInterface'

export const useEventData = (
  selectedOrg: Org | null,
  allCauses: Cause[],
  selectedEvent: Event | null
) => {
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([])
  const [pastEvents, setPastEvents] = useState<Event[]>([])
  const [eventDonations, setEventDonations] = useState<Donation[]>([])
  const [causes, setCauses] = useState<Cause[]>([])
  const lastOrgIdRef = useRef<string | null>(null)
  const subscriptionRef = useRef<(() => void) | null>(null)
  const processingRef = useRef<boolean>(false)

  useEffect(() => {
    if (!selectedOrg) {
      lastOrgIdRef.current = null
      processingRef.current = false
      if (subscriptionRef.current) {
        subscriptionRef.current()
        subscriptionRef.current = null
      }
      return
    }
    const orgKey = selectedOrg?.id ?? null
    if (lastOrgIdRef.current === orgKey || processingRef.current) return

    lastOrgIdRef.current = orgKey
    processingRef.current = true

    if (subscriptionRef.current) {
      subscriptionRef.current()
    }

    const sub = getEvents(selectedOrg, (upcoming, past) => {
      setUpcomingEvents(upcoming)
      setPastEvents(past)
    })
    subscriptionRef.current = sub
    processingRef.current = false

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current()
        subscriptionRef.current = null
      }
      processingRef.current = false
    }
  }, [selectedOrg?.id])

  useEffect(() => {
    if (!selectedEvent || allCauses.length === 0) return
    const causeIds = selectedEvent.causes || []
    const filtered = getOurCauses(causeIds, allCauses)
    setCauses(filtered)
    const sub = getEventDonations(selectedEvent, (donations) => {
      setEventDonations(donations)
    })
    return () => {
      if (sub) sub()
    }
  }, [selectedEvent?.id, allCauses.length])

  return {
    upcomingEvents,
    setUpcomingEvents,
    pastEvents,
    setPastEvents,
    eventDonations,
    setEventDonations,
    causes,
  } satisfies UseEventDataReturn
}
